import { useEffect, useRef, useState, type FormEvent, type Ref } from "react";
import type { NoroshiAccountSession, NoroshiReservationAccount } from "./_props";

type Phase = "phone" | "code" | "profile";

type ApiResponse = Partial<NoroshiAccountSession> & {
  error?: string;
  id?: string;
  echo?: string;
};

export type NoroshiAuthResult = {
  noroshiToken: string;
  account: NoroshiReservationAccount;
};

const ERRORS: Record<string, string> = {
  invalid_phone: "携帯電話番号（070, 080, 090）を入力してください",
  rate_limited: "送信回数の上限に達しました。時間をおいて再度お試しください",
  sms_failed: "SMSが送信出来ませんでした。電話番号をご確認の上、再度お願いします",
  expired: "認証の有効期限が切れました。電話番号からやり直してください",
  invalid_code: "認証コードが間違っています。",
  invalid_reservation_profile: "セイ・メイを入力してください。",
  unauthorized: "ログインの有効期限が切れました。電話番号からやり直してください",
  not_found: "通信に失敗しました。時間をおいて再度お試しください",
};

function messageFor(error: string | undefined, fallback: string): string {
  if (!error) return fallback;
  return ERRORS[error] ?? fallback;
}

function resultFrom(body: ApiResponse): NoroshiAuthResult | null {
  if (!body.noroshiToken || !body.account?.id || !body.account.phoneNumber) return null;
  return { noroshiToken: body.noroshiToken, account: body.account };
}

type AuthCustomer = {
  sei: string;
  mei: string;
  seiKana: string;
  meiKana: string;
};

function reservationCustomerFromAccount(
  account: Pick<NoroshiReservationAccount, "name" | "kana">,
): AuthCustomer {
  const [sei, mei] = splitName(account.name);
  const [seiKana, meiKana] = splitName(account.kana);
  return { sei, mei, seiKana, meiKana };
}

function splitName(value: string | null): [string, string] {
  const [first = "", ...rest] = value?.trim().split(/\s+/).filter(Boolean) ?? [];
  return [first, rest.join(" ")];
}

export function NoroshiPhoneAuthModal({
  origin,
  category = "noroshi_reservation",
  sourceId,
  completeOnCode = false,
  initialSession,
  onAuthenticated,
  onClose,
}: {
  origin: string;
  category?: string;
  sourceId?: string;
  completeOnCode?: boolean;
  initialSession?: NoroshiAuthResult | null;
  onAuthenticated: (result: NoroshiAuthResult) => void;
  onClose: () => void;
}) {
  const initialCustomer = initialSession
    ? reservationCustomerFromAccount(initialSession.account)
    : { sei: "", mei: "", seiKana: "", meiKana: "" };
  const [phase, setPhase] = useState<Phase>(initialSession ? "profile" : "phone");
  const [session, setSession] = useState<NoroshiAuthResult | null>(initialSession ?? null);
  const [phoneNumber, setPhoneNumber] = useState(initialSession?.account.phoneNumber ?? "");
  const [code, setCode] = useState("");
  const [verifyId, setVerifyId] = useState("");
  const [echo, setEcho] = useState<string | undefined>();
  const [familyName, setFamilyName] = useState(initialCustomer.sei);
  const [givenName, setGivenName] = useState(initialCustomer.mei);
  const [familyNameKana, setFamilyNameKana] = useState(initialCustomer.seiKana);
  const [givenNameKana, setGivenNameKana] = useState(initialCustomer.meiKana);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const profileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const target =
      phase === "phone"
        ? phoneRef.current
        : phase === "code"
          ? codeRef.current
          : profileRef.current;
    target?.focus();
  }, [phase]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function submitPhone(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`${origin}/api/verifies`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber,
          category,
          sourceId,
          returnTo: window.location.href,
        }),
      });
      const body = (await response.json().catch(() => ({}))) as ApiResponse;
      if (!response.ok || !body.id) {
        setError(messageFor(body.error, "SMSを送信できませんでした"));
        return;
      }
      setVerifyId(body.id);
      setEcho(body.echo);
      setPhase("code");
    } catch {
      setError("通信に失敗しました。時間をおいて再度お試しください");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`${origin}/api/verifies/${encodeURIComponent(verifyId)}`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ authenticationCode: code }),
      });
      const body = (await response.json().catch(() => ({}))) as ApiResponse;
      const authenticated = resultFrom(body);
      if (!response.ok || !authenticated) {
        if (body.error === "expired") {
          setPhase("phone");
          setVerifyId("");
          setCode("");
        }
        setError(messageFor(body.error, "認証できませんでした"));
        return;
      }
      const customer = reservationCustomerFromAccount(authenticated.account);
      setSession(authenticated);
      setPhoneNumber(authenticated.account.phoneNumber);
      setFamilyName(customer.sei);
      setGivenName(customer.mei);
      setFamilyNameKana(customer.seiKana);
      setGivenNameKana(customer.meiKana);
      onAuthenticated(authenticated);
      if (completeOnCode) {
        onClose();
        return;
      }
      setPhase("profile");
    } catch {
      setError("通信に失敗しました。時間をおいて再度お試しください");
    } finally {
      setBusy(false);
    }
  }

  async function submitProfile(event: FormEvent) {
    event.preventDefault();
    if (busy || !session) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`${origin}/api/session`, {
        method: "PATCH",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ familyName, givenName, familyNameKana, givenNameKana }),
      });
      const body = (await response.json().catch(() => ({}))) as ApiResponse;
      const updated = resultFrom(body);
      if (!response.ok || !updated) {
        if (response.status === 401) {
          setSession(null);
          setPhase("phone");
          setVerifyId("");
          setCode("");
        }
        setError(messageFor(body.error, "予約者情報を保存できませんでした"));
        return;
      }
      onAuthenticated(updated);
      onClose();
    } catch {
      setError("通信に失敗しました。時間をおいて再度お試しください");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (busy || !verifyId) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(
        `${origin}/api/verifies/${encodeURIComponent(verifyId)}/resend`,
        {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        },
      );
      const body = (await response.json().catch(() => ({}))) as ApiResponse;
      if (!response.ok) {
        setError(messageFor(body.error, "再送できませんでした"));
        return;
      }
      setEcho(body.echo);
    } catch {
      setError("通信に失敗しました。時間をおいて再度お試しください");
    } finally {
      setBusy(false);
    }
  }

  const eyebrow = phase === "profile" ? "予約者情報" : "電話番号認証";
  const title =
    phase === "phone"
      ? "予約を続ける前に"
      : phase === "code"
        ? "認証コードを入力"
        : "予約に使うお名前";

  return (
    <div className="fixed inset-0 z-[10000]">
      <button
        type="button"
        aria-label="閉じる"
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!busy) onClose();
        }}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="noroshi-auth-title"
          className="pointer-events-auto max-h-[100dvh] w-full max-w-md animate-[noroshi-auth-drop_480ms_cubic-bezier(0.22,1.25,0.36,1)] overflow-y-auto rounded-b-3xl bg-white px-6 pb-8 pt-[max(20px,env(safe-area-inset-top))] shadow-2xl"
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-wide text-red-600">{eyebrow}</p>
              <h2 id="noroshi-auth-title" className="mt-1 text-[22px] font-bold leading-snug text-gray-950">
                {title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="mt-1 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 disabled:opacity-40"
              aria-label="モーダルを閉じる"
            >
              <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {phase === "phone" && (
            <form onSubmit={submitPhone} className="space-y-4">
              <p className="text-sm leading-relaxed text-gray-500">
                ご本人確認のため電話番号を教えてください。SMSで認証コードが届きます。
              </p>
              <label className="block">
                <span className="sr-only">携帯電話番号</span>
                <input
                  ref={phoneRef}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={13}
                  required
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  placeholder="09012345678"
                  className="h-14 w-full rounded-none border-0 border-b border-gray-900 bg-transparent text-center text-3xl tracking-[0.2em] text-gray-950 outline-none"
                />
              </label>
              {error && <p className="text-center text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="flex h-12 w-full items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white shadow-sm disabled:bg-red-200"
              >
                {busy ? "送信しています…" : "SMSを送信する"}
              </button>
              <p className="pt-1 text-center text-xs leading-6 text-gray-400">
                続けることで、以下に同意したものとみなされます。
                <br />
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-gray-500 underline"
                >
                  利用規約
                </a>
                <span className="px-1">/</span>
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-gray-500 underline"
                >
                  プライバシーポリシー
                </a>
              </p>
            </form>
          )}

          {phase === "code" && (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm leading-relaxed text-gray-500">
                電話番号に認証コードを送りました。届いた6桁を入力してください。
              </p>
              <label className="block">
                <span className="sr-only">認証コード</span>
                <input
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="h-14 w-full rounded-none border-0 border-b border-gray-900 bg-transparent text-center text-3xl tracking-[0.4em] text-gray-950 outline-none"
                />
              </label>
              {echo && <p className="text-center text-xs text-gray-400">local OTP: {echo}</p>}
              {error && <p className="text-center text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="flex h-12 w-full items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white shadow-sm disabled:bg-red-200"
              >
                {busy ? "認証しています…" : "認証する"}
              </button>
              <button
                type="button"
                onClick={resend}
                disabled={busy}
                className="w-full text-center text-sm font-semibold text-gray-500 disabled:opacity-40"
              >
                認証コードを再送する
              </button>
            </form>
          )}

          {phase === "profile" && (
            <form onSubmit={submitProfile} className="space-y-4">
              <p className="text-sm leading-relaxed text-gray-500">
                この予約と次回以降の予約に使います。電話番号を変える場合は、あらためて認証してください。
              </p>
              <div className="rounded-2xl bg-gray-50 px-4 py-3">
                <p className="text-[10px] font-bold tracking-wide text-gray-400">認証済み電話番号</p>
                <p className="mt-1 font-mono text-sm font-semibold text-gray-800">{phoneNumber}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ProfileField
                  ref={profileRef}
                  label="セイ"
                  value={familyNameKana}
                  onChange={setFamilyNameKana}
                  autoComplete="family-name"
                  required
                />
                <ProfileField
                  label="メイ"
                  value={givenNameKana}
                  onChange={setGivenNameKana}
                  autoComplete="given-name"
                  required
                />
                <ProfileField label="姓（任意）" value={familyName} onChange={setFamilyName} />
                <ProfileField label="名（任意）" value={givenName} onChange={setGivenName} />
              </div>
              {error && <p className="text-center text-sm text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={busy || !familyNameKana.trim() || !givenNameKana.trim()}
                className="flex h-12 w-full items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white shadow-sm disabled:bg-red-200"
              >
                {busy ? "保存しています…" : "保存して予約へ戻る"}
              </button>
            </form>
          )}
        </section>
      </div>
      <style>{`
        @keyframes noroshi-auth-drop {
          0% { transform: translateY(-110%); }
          72% { transform: translateY(10px); }
          100% { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ProfileField({
  ref,
  label,
  value,
  onChange,
  autoComplete,
  required = false,
}: {
  ref?: Ref<HTMLInputElement>;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-semibold text-gray-600">
      {label}
      <input
        ref={ref}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        maxLength={50}
        className="mt-1 h-11 w-full rounded-xl border border-gray-200 px-3 text-base font-normal text-gray-950 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
      />
    </label>
  );
}
